import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";

import type { InstallPlatform, PlatformId } from "./tsh-platform";
import { platformStore } from "./tsh-platform";

// The pure half of this control lives next door, so `content.ts` and the Deno
// tests can reach the detection and the store without a custom-element
// registry. Re-exported here because the bar is the name a reader looks under.
export {
  DEFAULT_PLATFORM,
  detectPlatform,
  initialPlatform,
  isPlatformId,
  PLATFORM_IDS,
  platformsProp,
  platformStore,
} from "./tsh-platform";
export type { InstallPlatform, PlatformHints, PlatformId } from "./tsh-platform";

// The platform bar: three tabs above the install command, one of them already
// the reader's own.
//
// It is a SEGMENTED CONTROL rather than three commands stacked down the page,
// and that is the whole design decision. A page that prints every platform's
// one-liner asks the reader to match their machine against a list before they
// can copy anything, and it prints two lines that are wrong for them. A page
// that prints one asks the Windows reader to believe there is no Windows
// build. Preselecting from the user agent means the common case is a command
// that is already correct, and the tabs are there for the reader who is
// reading on one machine and installing on another.
//
// Selection lives in the shared store, not in this element: the page mounts
// two of these — hero and CTA — and they must not disagree.
@component({ name: "tsh-platform-bar" })
export class TshPlatformBar extends LonaWebComponent {
  private $bar: HTMLElement = this.$("bar");
  private $tabs: Map<PlatformId, HTMLElement> = new Map();
  private unsubscribe: Option<() => void> = null;

  /**
   * Draw one tab per platform.
   *
   * Rebuilt rather than diffed: the table is three rows long and arrives once,
   * and reusing tabs across a re-bind is how a stale label survives into a
   * node that never carried it.
   */
  bind(platforms: InstallPlatform[]): void {
    this.$bar.replaceChildren();
    this.$tabs.clear();

    for (const platform of platforms) {
      const $tab = document.createElement("button");
      $tab.type = "button";
      $tab.setAttribute("role", "tab");
      $tab.setAttribute("data-platform", platform.id);

      const $label = document.createElement("span");
      $label.className = "label";
      $label.textContent = platform.label;
      $tab.appendChild($label);

      if (platform.detail !== "") {
        const $detail = document.createElement("span");
        $detail.className = "detail";
        $detail.textContent = platform.detail;
        $tab.appendChild($detail);
      }

      GESTURE_MANAGER.addPointerEvent($tab, {
        onClick: () => platformStore.set(platform.id),
      });

      this.$bar.appendChild($tab);
      this.$tabs.set(platform.id, $tab);
    }

    // One tab is not a choice, and a control offering none is furniture.
    this.toggleAttribute("hidden", this.$tabs.size < 2);
    this.paintSelection();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.unsubscribe ??= platformStore.subscribe(() => this.paintSelection());
    this.paintSelection();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    // The store outlives the element — these components are pooled and
    // recycled — so a subscription left behind holds a detached tab tree alive
    // and repaints it on every click for the rest of the page's life.
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /** `aria-selected` IS the state: the styling keys off it, so they cannot drift. */
  private paintSelection(): void {
    const selected = platformStore.get();
    for (const [id, $tab] of this.$tabs) {
      $tab.setAttribute("aria-selected", String(id === selected));
    }
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(
          --tsh-mono,
          "SFMono-Regular",
          "Cascadia Mono",
          "Roboto Mono",
          "Liberation Mono",
          ui-monospace,
          monospace
        );
      }

      /* This component is nested inside a themed hero or CTA. It deliberately
         does not install tshVars on its own host: a local default sheet would
         override the palette inherited across this shadow boundary. */
      #bar {
        display: grid;
        grid-auto-columns: minmax(0, 1fr);
        grid-auto-flow: column;
        box-sizing: border-box;
        width: 100%;
        max-width: var(--tsh-panel, 680px);
        border: 1px solid var(--tsh-border, #e5e5e5);
        border-radius: var(--tsh-radius, 10px);
        background: color-mix(
          in srgb,
          var(--tsh-surface, #fafafa) 70%,
          var(--tsh-bg, #ffffff)
        );
        overflow: hidden;
      }

      button {
        display: flex;
        box-sizing: border-box;
        align-items: baseline;
        justify-content: center;
        gap: 8px;
        min-width: 0;
        padding: 9px 12px;
        border: 0;
        border-left: 1px solid var(--tsh-border, #e5e5e5);
        background: transparent;
        font: inherit;
        font-size: calc(var(--small-size, 0.53125rem) / 0.7);
        text-align: center;
        white-space: nowrap;
        cursor: pointer;
        color: var(--tsh-muted, #525252);
      }

      button:first-child {
        border-left: 0;
      }

      button:hover {
        background: color-mix(
          in srgb,
          var(--tsh-fg, #171717) 5%,
          transparent
        );
        color: var(--tsh-fg, #171717);
      }

      button:focus-visible {
        z-index: 1;
        outline: 2px solid var(--tsh-term-node, #4f5fbd);
        outline-offset: -3px;
      }

      button[aria-selected="true"] {
        background: var(--tsh-bg, #ffffff);
        box-shadow: inset 0 -2px 0 var(--tsh-fg, #171717);
        color: var(--tsh-fg, #171717);
        font-weight: 700;
      }

      /* The qualifier stays faint in every state: it is an aside about the
         hardware, and a selected tab that brightens both halves reads as two
         labels rather than as a label and its footnote. */
      .detail {
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 0.85em;
        color: var(--tsh-faint, #9e9e9e);
      }

      /* On a narrow screen the label is the part that must survive. */
      @media (max-width: 560px) {
        .detail {
          display: none;
        }

        button {
          padding: 9px;
        }
      }

      :host([hidden]) {
        display: none !important;
      }
    `,
  ];

  static $html = html`
    <div id="bar" role="tablist" aria-label="Install platform"></div>
  `;
}
